import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Divider, Flex, Space, Typography } from "antd"
import { connect } from "react-redux"

import Button from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import GlobalFilterTestData from "../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../Components/ManageColumns"
import Table from "../../Components/Table"
import Icon from "../../Icon"
import { modalConfig } from "../../theme"
import { adminUsersColumnIconMap, serializeData } from "../../Utils/utils"
import InviteUserModal from "./components/InviteUserModal"
import { getActionColumn, getColumns } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

const { Title, Text } = Typography

const pageSize = 50

const pinnedColumns = ["Status"]

const statusOptions = ["invite_sent", "active", "expired"]

const AdminUsersPage = ({
  isMobile,
  fetching,
  result,
  statistics,
  inviteRoles,
  saveFetching,
  saveResult,
  resendResult,
  deleteResult,
  filters,
  visibleFilters,
  columnsAdmin,
  defaultColumnsAdmin,
  isFiltersDefault,
  test_data,
  exportFetching,
  exportResult,
  ...props
}) => {
  const { notification, modal } = App.useApp()

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { width: "fit-content", maxWidth: 480, gridTemplateColumns: "max-content max-content" }
  }

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const [inviteModalOpen, setInviteModalOpen] = useState(false)
  const [editUserData, setEditUserData] = useState(null)

  const { count, results } = result

  useEffect(() => {
    document.title = "Admin users - Chargeback"
  }, [])

  useEffect(() => {
    props.getAdminUsersInviteRoles()
  }, [])

  useEffect(() => {
    // Admin users number is static and only refetched when the Test on/off filter changes
    props.getAdminUsersCount({ test_data })
  }, [test_data])

  useEffect(() => {
    props.getAdminUsers({
      page: pageNumber,
      page_size: pageSize,
      ordering,
      test_data,
      ...serializeData(filters)
    })
  }, [pageNumber, ordering, test_data, filters])

  function handleSelectRow(record, type) {
    if (type === "settings") {
      setEditUserData(record)
      setInviteModalOpen(true)
      return
    }
    if (type === "resend") {
      props.resendAdminUserInvite(record?.id)
      return
    }
    if (type === "delete") {
      modal.confirm({
        ...modalConfig("confirm_delete"),
        className: "delete-user-confirm-modal",
        centered: !isMobile,

        title: "Confirm deleting",
        content:
          "Are you sure you want to delete this admin user? The user will no longer have access to the admin panel. This action cannot be undone. However, you can invite this user again later if needed.",

        cancelText: "Cancel",
        okText: "Delete",
        onOk() {
          props.deleteAdminUser(record?.id)
        }
      })
      return
    }
  }

  const handleOpenInviteModal = () => {
    setEditUserData(null)
    setInviteModalOpen(true)
  }

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false)
    setEditUserData(null)
  }

  const handleSubmitInviteForm = ({ name, email, role }) => {
    if (editUserData?.id) {
      props.updateAdminUserRole({ id: editUserData.id, role })
      return
    }
    props.inviteAdminUser({ name, email, role })
  }

  useEffect(() => {
    if (saveResult) {
      handleCloseInviteModal()
      notification.success({
        message: saveResult?.detail || "Changes saved",
        ...notificationConfig
      })
      props.getAdminUsers({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        test_data,
        ...serializeData(filters)
      })
      props.getAdminUsersCount({ test_data })
      props.adminUsersSetProp("saveResult", null)
    }
  }, [saveResult])

  useEffect(() => {
    if (resendResult) {
      notification.success({
        message: resendResult?.detail || "Invitation has been resent",
        ...notificationConfig
      })
      props.adminUsersSetProp("resendResult", null)
    }
  }, [resendResult])

  useEffect(() => {
    if (deleteResult) {
      notification.success({
        message:
          deleteResult?.message?.detail || deleteResult?.message || "Admin user has been deleted",
        ...notificationConfig
      })
      props.getAdminUsers({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        test_data,
        ...serializeData(filters)
      })
      props.getAdminUsersCount({ test_data })
      props.adminUsersSetProp("deleteResult", null)
    }
  }, [deleteResult])

  const handleExport = useCallback(
    (type) => {
      props.getAdminUsersExport({
        ...(type !== "all" && serializeData(filters)),
        export_all: type === "all"
      })
    },
    [filters]
  )

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description: exportResult?.message,
        ...notificationConfig
      })
      props.adminUsersSetProp("exportResult", null)
    }
  }, [exportResult])

  const handleChangeFilter = (newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters(newData)
    } else {
      setPageNumber(1)
      props.changeFilters(newData)
    }
  }

  const roleFilterOptions = useMemo(
    () => (inviteRoles || []).map(({ id, label, value }) => ({ id, name: label, value })),
    [inviteRoles]
  )

  const handleChangeColumns = (newColumns) => {
    props.changeColumns(newColumns)
  }

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: count ? "max-content" : 1100,
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      }
    }),
    [count, pageNumber]
  )

  const columns = useMemo(() => {
    const allColumns = getColumns({ inviteRoles })

    return [
      allColumns["status"],
      ...columnsAdmin.filter(({ active }) => active).map(({ field_id }) => allColumns[field_id]),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [pageNumber, columnsAdmin, inviteRoles])

  const handleChangeTable = (pagination, filters, sorter) => {
    let newOrdering = null
    if (sorter?.order) {
      newOrdering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
    }
    setPageNumber(pagination.current)
    setOrdering(newOrdering)
  }

  return (
    <div className="users-page">
      <Title level={3} style={{ textAlign: "left", marginBottom: 16 }}>
        {`Admin users: ${statistics?.count ?? "..."}`}
      </Title>

      <Divider />

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 20px" }}>
        <Filters
          visibleFilters={visibleFilters}
          allFilters={defaultColumnsAdmin}
          filters={filters}
          filtersType="admin_users"
          filterOptions={{ status: statusOptions, role: roleFilterOptions }}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={props.clearAdminUsersFilters}
          columnIconMap={adminUsersColumnIconMap}
        />

        <Space style={{ flexShrink: 0 }}>
          <Button type="primary" className="flex" onClick={handleOpenInviteModal}>
            Invite user
          </Button>

          <GlobalFilterTestData setPageNumber={setPageNumber} />

          <ExportDropdown loading={exportFetching} onExport={handleExport} isAdmin />

          <ManageColumn
            myColumns={columnsAdmin}
            onChangeColumns={handleChangeColumns}
            pinnedColumns={pinnedColumns}
            iconsType={"adminUsers"}
          />
        </Space>
      </Flex>

      <Table
        columns={columns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        locale={{
          emptyText: !statistics?.count ? (
            <EmptyComponent type="search" />
          ) : (
            <EmptyComponent type="search">
              <Button
                className="flex"
                icon={<Icon type="menu_search" />}
                onClick={props.clearAdminUsersFilters}
              >
                Clear search
              </Button>
            </EmptyComponent>
          )
        }}
      />

      <InviteUserModal
        open={inviteModalOpen}
        onClose={handleCloseInviteModal}
        editData={editUserData}
        inviteRoles={inviteRoles}
        saveFetching={saveFetching}
        onSubmit={handleSubmitInviteForm}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  isMobile: state.nav.isMobile,

  fetching: state.adminUsers.fetching,
  result: state.adminUsers.result,
  statistics: state.adminUsers.statistics,
  inviteRoles: state.adminUsers.inviteRoles,
  saveFetching: state.adminUsers.saveFetching,
  saveResult: state.adminUsers.saveResult,
  resendResult: state.adminUsers.resendResult,
  deleteResult: state.adminUsers.deleteResult,

  exportFetching: state.adminUsers.exportFetching,
  exportResult: state.adminUsers.exportResult,

  filters: state.adminUsers.filters,
  visibleFilters: state.adminUsers.visibleFilters,
  isFiltersDefault: selectAreFiltersDefault(state),

  columnsAdmin: state.adminUsers.columnsAdmin,
  defaultColumnsAdmin: state.adminUsers.defaultColumnsAdmin,

  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getAdminUsers: (params) => dispatch(Creators.getAdminUsersRequest(params)),
  getAdminUsersCount: (params) => dispatch(Creators.getAdminUsersCountRequest(params)),
  getAdminUsersInviteRoles: (params) => dispatch(Creators.getAdminUsersInviteRolesRequest(params)),
  getAdminUsersExport: (params) => dispatch(Creators.getAdminUsersExportRequest(params)),
  updateAdminUserRole: (payload) => dispatch(Creators.updateAdminUserRoleRequest(payload)),
  inviteAdminUser: (payload) => dispatch(Creators.inviteAdminUserRequest(payload)),
  resendAdminUserInvite: (id) => dispatch(Creators.resendAdminUserInviteRequest(id)),
  deleteAdminUser: (id) => dispatch(Creators.deleteAdminUserRequest(id)),

  adminUsersSetProp: (key, value) => dispatch(Creators.adminUsersSetProp(key, value)),

  changeFilters: (filters) => dispatch(Creators.adminUsersSetProp("filters", filters)),
  changeVisibleFilters: (visibleFilters) =>
    dispatch(Creators.adminUsersSetProp("visibleFilters", visibleFilters)),
  clearAdminUsersFilters: () => dispatch(Creators.clearAdminUsersFilters()),

  changeColumns: (columns) => dispatch(Creators.adminUsersSetProp("columnsAdmin", columns))
})

export default connect(mapStateToProps, mapDispatchToProps)(AdminUsersPage)
